import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Moon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { currencySymbol } from "@/i18n/translations";

const FEATURE_KEYS = ["coachTitle", "thisWeek", "exportData", "reminders"];

export default function Paywall() {
  const { t } = useI18n();
  const { paywallOpen, closePaywall, plans } = useSubscription();
  const [selected, setSelected] = useState("vespera_pro_yearly");
  const [loading, setLoading] = useState(false);

  const priceFor = (lk) => plans.find((p) => p.lookup_key === lk);
  const monthly = priceFor("vespera_pro_monthly");
  const yearly = priceFor("vespera_pro_yearly");
  const fmt = (p) => (p ? `${currencySymbol(p.currency.toUpperCase())}${(p.amount / 100).toFixed(p.amount % 100 ? 2 : 0)}` : "—");

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout error");
      setLoading(false);
    }
  };

  const PlanCard = ({ lk, price, per, badge }) => {
    const active = selected === lk;
    return (
      <button
        data-testid={`plan-${lk}`}
        onClick={() => setSelected(lk)}
        className="relative w-full text-left rounded-xl border p-4 transition-colors"
        style={{ borderColor: active ? "hsl(var(--primary))" : "rgba(255,255,255,0.12)", backgroundColor: active ? "hsl(var(--primary) / 0.08)" : "transparent" }}
      >
        {badge && (
          <span className="absolute -top-2 right-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
            {badge}
          </span>
        )}
        <div className="flex items-center justify-between">
          <span className="font-semibold">{lk.includes("yearly") ? t("yearly") : t("monthly")}</span>
          <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-primary bg-primary" : "border-white/30"}`}>
            {active && <Check className="h-3 w-3 text-primary-foreground" />}
          </span>
        </div>
        <p className="mt-1 font-display text-2xl font-extrabold">
          {fmt(price)}<span className="text-sm font-medium text-muted-foreground">{per}</span>
        </p>
      </button>
    );
  };

  return (
    <Dialog open={paywallOpen} onOpenChange={(o) => !o && closePaywall()}>
      <DialogContent className="bg-popover border-white/10 max-w-md" data-testid="paywall-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
          </div>
          <DialogTitle className="font-display text-2xl text-center">{t("paywallTitle")}</DialogTitle>
          <DialogDescription className="text-center">{t("paywallSubtitle")}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 my-2">
          {FEATURE_KEYS.map((k) => (
            <li key={k} className="flex items-center gap-2.5 text-sm">
              <Check className="h-4 w-4 text-primary shrink-0" />
              <span>{t(k)}</span>
            </li>
          ))}
        </ul>

        <div className="grid grid-cols-2 gap-3">
          <PlanCard lk="vespera_pro_monthly" price={monthly} per={t("perMonth")} />
          <PlanCard lk="vespera_pro_yearly" price={yearly} per={t("perYear")} badge={t("savePct")} />
        </div>

        <Button data-testid="paywall-subscribe-btn" onClick={checkout} disabled={loading} className="w-full h-12 font-semibold mt-1">
          {loading ? t("redirecting") : t("startTrial")}
        </Button>
        <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
          <Moon className="h-3 w-3" /> {t("freeTrialNote")}
        </p>
      </DialogContent>
    </Dialog>
  );
}
